import { FC, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { classNames } from 'primereact/utils';
import FormInputText from '../../components/form/FormInputText';
import { OverrideSettingForm } from '../waterfall-editor/OverrideSettingForm';
import { OverrideSettingViewModel } from '../../models/waterfall';
import { CreateOrUpdateVoxTestCaseViewModel, VoxOverridesMap, VoxTestCaseViewModel } from '../../models/voxTestCase';

interface OverrideRow {
  key: string;
  setting: OverrideSettingViewModel | null;
}

interface TestCaseFormValues {
  vendor: string;
  name: string;
  description: string;
  active: boolean;
}

interface VoxTestCaseDialogProps {
  visible: boolean;
  onClose: () => void;
  onSave: (payload: CreateOrUpdateVoxTestCaseViewModel) => Promise<void>;
  initialTestCase?: VoxTestCaseViewModel | null;
  vendors: string[];
  saving?: boolean;
}

const validationSchema = yup.object().shape({
  vendor: yup.string().trim().required('Vendor is required'),
  name: yup.string().trim().required('Name is required').max(100, 'Name must be 100 characters or less'),
  description: yup.string().trim().max(500, 'Description must be 500 characters or less').default(''),
  active: yup.boolean().default(true),
});

const rowKey = (): string => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const buildInitialRows = (overrides: VoxOverridesMap | undefined): OverrideRow[] =>
  Object.entries(overrides ?? {}).map(([fieldName, value]) => ({
    key: rowKey(),
    setting: { fieldName, value },
  }));

const toOverrides = (rows: OverrideRow[]): VoxOverridesMap => {
  const overrides: VoxOverridesMap = {};
  rows.forEach(({ setting }) => {
    if (setting?.fieldName && setting.value != null && String(setting.value).trim() !== '') {
      overrides[setting.fieldName] = String(setting.value);
    }
  });
  return overrides;
};

export const VoxTestCaseDialog: FC<VoxTestCaseDialogProps> = ({
  visible,
  onClose,
  onSave,
  initialTestCase,
  vendors,
  saving = false,
}) => {
  const [rows, setRows] = useState<OverrideRow[]>(() => buildInitialRows(initialTestCase?.overrides));

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TestCaseFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      vendor: initialTestCase?.vendor ?? '',
      name: initialTestCase?.name ?? '',
      description: initialTestCase?.description ?? '',
      active: initialTestCase?.active ?? true,
    },
  });

  const selectedVendor = watch('vendor');
  const vendorOptions = vendors.map((vendor) => ({ label: vendor, value: vendor }));

  const addOverrideRow = () => setRows((previous) => [...previous, { key: rowKey(), setting: null }]);

  const updateOverrideRow = (key: string, setting: OverrideSettingViewModel | null) =>
    setRows((previous) => previous.map((row) => (row.key === key ? { ...row, setting } : row)));

  const deleteOverrideRow = (key: string) => setRows((previous) => previous.filter((row) => row.key !== key));

  const onSubmit = async (values: TestCaseFormValues) => {
    await onSave({
      voxTestCaseId: initialTestCase?.voxTestCaseId ?? null,
      vendor: values.vendor,
      name: values.name.trim(),
      description: values.description?.trim() || null,
      overrides: toOverrides(rows),
      active: values.active,
    });
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" className="p-button-outlined p-button-sm" onClick={onClose} disabled={saving} />
      <Button
        label={initialTestCase ? 'Save' : 'Create'}
        className="p-button-primary p-button-sm"
        onClick={handleSubmit(onSubmit)}
        loading={saving}
      />
    </div>
  );

  return (
    <Dialog
      header={initialTestCase ? 'Edit Test Case' : 'Create Test Case'}
      visible={visible}
      onHide={onClose}
      style={{ width: '55rem' }}
      footer={footer}
      modal
      dismissableMask={false}
    >
      <form className="p-fluid p-input-filled" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="grid">
          <div className="col-12 md:col-4">
            <label className="font-medium text-sm mb-2 block">
              Vendor <span className="text-red-500">*</span>
            </label>
            <Controller
              control={control}
              name="vendor"
              render={({ field }) => (
                <Dropdown
                  id={field.name}
                  value={field.value}
                  options={vendorOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select vendor"
                  onChange={(e) => field.onChange(e.value)}
                  className={classNames({ 'p-invalid': !!errors.vendor })}
                  appendTo={document.body}
                  disabled={!!initialTestCase}
                />
              )}
            />
            {errors.vendor && <small className="p-error">{errors.vendor.message}</small>}
          </div>
          <div className="col-12 md:col-5">
            <FormInputText
              control={control}
              errors={errors}
              field="name"
              label="Name"
              placeholder="Enter test case name"
              required
              maxLength={100}
            />
          </div>
          <div className="col-12 md:col-3">
            <label className="font-medium text-sm mb-2 block">Active</label>
            <Controller
              control={control}
              name="active"
              render={({ field }) => (
                <InputSwitch checked={field.value} onChange={(e) => field.onChange(e.value ?? false)} />
              )}
            />
          </div>
          <div className="col-12">
            <FormInputText
              control={control}
              errors={errors}
              field="description"
              label="Description"
              placeholder="Enter description (optional)"
              maxLength={500}
            />
          </div>
        </div>

        <div className="flex items-center justify-between mb-2 mt-2">
          <label className="font-medium text-sm block">Overrides</label>
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add override"
            className="p-button-text p-button-sm"
            onClick={addOverrideRow}
            disabled={!selectedVendor}
          />
        </div>
        {!selectedVendor ? (
          <div className="text-gray-500 text-sm italic">Select a vendor to configure overrides.</div>
        ) : rows.length === 0 ? (
          <div className="text-gray-500 text-sm italic">No overrides yet. Click "Add override" to add one.</div>
        ) : (
          rows.map((row) => (
            <div key={row.key} className="mb-2">
              <OverrideSettingForm
                initialValue={row.setting || undefined}
                onChange={(setting) => updateOverrideRow(row.key, setting)}
                onDelete={() => deleteOverrideRow(row.key)}
                vendorName={selectedVendor}
              />
            </div>
          ))
        )}
      </form>
    </Dialog>
  );
};
